import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import usePageMeta from "@/hooks/usePageMeta";
import {
  allGenres,
  allLanguages,
  allMovies,
  allRegions,
  allTypes,
  allYears,
  countActiveFilters,
  filterMovies,
  paginate,
  sortOptions,
} from "@/lib/movies";
import type { MovieFilter } from "@/lib/types";
import FilterBar from "./components/FilterBar";

const PAGE_SIZE = 9;

const options = {
  type: allTypes,
  region: allRegions,
  genre: allGenres,
  year: allYears,
  language: allLanguages,
};

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();

  const readValue = (key: string, list: string[]) => {
    const value = searchParams.get(key) ?? "全部";
    return list.includes(value) ? value : "全部";
  };

  const sortParam = searchParams.get("sort") ?? "default";
  const filter: MovieFilter = {
    keyword: "",
    type: readValue("type", allTypes),
    region: readValue("region", allRegions),
    genre: readValue("genre", allGenres),
    year: readValue("year", allYears),
    language: readValue("language", allLanguages),
    sort: sortOptions.some((option) => option.value === sortParam) ? sortParam : "default",
  };

  const categoryName =
    filter.genre !== "全部"
      ? `${filter.genre}题材影视`
      : filter.type !== "全部"
        ? `${filter.type}作品`
        : filter.region !== "全部"
          ? `${filter.region}影视作品`
          : filter.year !== "全部"
            ? `${filter.year}年影视作品`
            : "全部影视作品";

  const results = useMemo(() => filterMovies(filter), [filter]);
  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const rawPage = Number(searchParams.get("page") ?? "1");
  const page = Math.min(Math.max(Number.isFinite(rawPage) ? rawPage : 1, 1), totalPages);
  const pageItems = paginate(results, page, PAGE_SIZE);
  const activeCount = countActiveFilters(filter);

  usePageMeta(
    `${categoryName} - 淫乱小镇影视内容`,
    `淫乱小镇${categoryName}分类，共收录 ${results.length} 部作品，可按类型、地区、年份、语言与题材组合筛选，附完整剧情介绍与深度解析。`,
    `淫乱小镇,${categoryName},影视分类,剧情介绍,影视分析`,
  );

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "全部") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    if (key !== "page") next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const handleReset = () => {
    const next = new URLSearchParams();
    if (filter.sort !== "default") next.set("sort", filter.sort);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main className="pt-16 md:pt-20">
        <section className="border-b border-background-200/70 bg-background-100/40">
          <div className="max-w-shell mx-auto px-4 md:px-8 py-8 md:py-11">
            <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-2 text-xs text-foreground-600">
              <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-700"></i>
              <Link to="/category" className="hover:text-accent-500 transition-colors cursor-pointer">
                影视分类
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-700"></i>
              <span className="text-foreground-300">{categoryName}</span>
            </nav>

            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-50">
              {categoryName}
            </h1>
            <p className="mt-3 max-w-3xl text-sm md:text-base leading-relaxed text-foreground-500">
              在这里按类型、地区、年份、语言与题材自由组合筛选。每部作品都提供完整的剧情摘要与解析文章，帮助你快速判断值不值得花两个小时。
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-600">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-stack-line text-accent-500"></i>
                当前结果 <strong className="text-foreground-200 font-label">{results.length}</strong> 部
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-database-2-line text-accent-500"></i>
                作品库共 {allMovies.length} 部在库
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-layout-grid-line text-accent-500"></i>
                每页 {PAGE_SIZE} 部
              </span>
            </div>
          </div>
        </section>

        <section className="max-w-shell mx-auto px-4 md:px-8 py-8 md:py-10">
          <FilterBar
            filter={filter}
            options={options}
            activeCount={activeCount}
            onChange={updateParam}
            onReset={handleReset}
          />

          <div className="mt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p className="text-xs text-foreground-500">
              共 <span className="text-foreground-100 font-label">{results.length}</span> 部作品
              {totalPages > 1 && (
                <span className="text-foreground-600">
                  {" "}
                  · 第 {page} / {totalPages} 页
                </span>
              )}
            </p>

            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-600 whitespace-nowrap">排序</span>
              <div className="relative">
                <select
                  value={filter.sort}
                  onChange={(event) => updateParam("sort", event.target.value)}
                  aria-label="排序方式"
                  className="appearance-none h-9 pl-3 pr-8 rounded-md bg-background-100 border border-background-300 text-xs text-foreground-200 focus:outline-none focus:border-accent-500 cursor-pointer"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value} className="bg-background-100">
                      {option.label}
                    </option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-foreground-500 pointer-events-none"></i>
              </div>
            </div>
          </div>

          {results.length === 0 ? (
            <div className="mt-6">
              <EmptyState
                icon="ri-filter-off-line"
                title="没有符合条件的作品"
                description="当前筛选条件组合过于严格。试着减少一个条件，或者直接重置筛选，回到完整作品库。"
                actionLabel="重置筛选条件"
                onAction={handleReset}
              />
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {pageItems.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          )}

          <Pagination
            page={page}
            totalPages={totalPages}
            onChange={(nextPage) => {
              updateParam("page", String(nextPage));
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}